/**
 * F006: Compliance Calendar Page
 *
 * Calendar/list page showing all compliance deadlines with 60/30/14/7-day
 * reminder indicators, overdue highlighting, and event management.
 *
 * Route: /compliance/calendar (registered by F005)
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  CheckCircle2,
  Plus,
  RefreshCw,
  ExternalLink,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";

// ============================================================================
// TYPES
// ============================================================================

type ReminderLevel = "7day" | "14day" | "30day" | "60day" | "overdue" | null;
type CalendarStatus = "upcoming" | "due" | "overdue" | "completed" | "cancelled";
type EventType = "renewal" | "lodgement" | "audit" | "training" | "review" | "other";
type StatusFilter = "all" | "pending" | "overdue" | "completed";

interface CalendarEvent {
  id: string;
  eventType: EventType;
  eventTitle: string;
  eventDescription: string | null;
  dueDate: string;
  calendarStatus: CalendarStatus;
  reminderLevel: ReminderLevel;
  daysUntilDue: number | null;
  completedDate: string | null;
  completionNotes: string | null;
  createdAt: string;
}

interface AccSummary {
  complianceYear: number;
  totalItems: number;
  completedItems: number;
  completionPercentage: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  renewal: "Renewal",
  lodgement: "Lodgement",
  audit: "Audit",
  training: "Training",
  review: "Review",
  other: "Other",
};

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "completed", label: "Completed" },
];

// Reminder level colour config
const REMINDER_COLOURS: Record<
  NonNullable<ReminderLevel>,
  { badge: string; row: string; label: string }
> = {
  overdue: {
    badge: "bg-red-600 text-white border-red-700",
    row: "bg-red-50 border-l-4 border-l-red-500",
    label: "OVERDUE",
  },
  "7day": {
    badge: "bg-red-100 text-red-700 border-red-300",
    row: "border-l-4 border-l-red-400",
    label: "7 days",
  },
  "14day": {
    badge: "bg-orange-100 text-orange-700 border-orange-300",
    row: "border-l-4 border-l-orange-400",
    label: "14 days",
  },
  "30day": {
    badge: "bg-yellow-100 text-yellow-700 border-yellow-300",
    row: "border-l-4 border-l-yellow-400",
    label: "30 days",
  },
  "60day": {
    badge: "bg-blue-100 text-blue-700 border-blue-300",
    row: "border-l-4 border-l-blue-400",
    label: "60 days",
  },
};

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  try {
    return format(parseISO(dateStr), "dd/MM/yyyy");
  } catch {
    return dateStr;
  }
}

function getDaysLabel(days: number | null): string {
  if (days === null) return "-";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Due today";
  return `${days}d`;
}

/**
 * Compute local reminderLevel from daysUntilDue and status.
 * Used as a fallback when API doesn't return reminderLevel.
 */
function computeReminderLevel(
  daysUntilDue: number | null,
  calendarStatus: CalendarStatus
): ReminderLevel {
  if (calendarStatus === "completed" || calendarStatus === "cancelled") return null;
  if (daysUntilDue === null) return null;
  if (daysUntilDue < 0) return "overdue";
  if (daysUntilDue <= 7) return "7day";
  if (daysUntilDue <= 14) return "14day";
  if (daysUntilDue <= 30) return "30day";
  if (daysUntilDue <= 60) return "60day";
  return null;
}

/**
 * Filter events based on the status filter tab.
 */
function filterEvents(events: CalendarEvent[], filter: StatusFilter): CalendarEvent[] {
  switch (filter) {
    case "pending":
      return events.filter(
        (e) =>
          e.calendarStatus === "upcoming" ||
          e.calendarStatus === "due"
      );
    case "overdue":
      return events.filter((e) => e.calendarStatus === "overdue");
    case "completed":
      return events.filter((e) => e.calendarStatus === "completed");
    default:
      return events;
  }
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

interface ReminderBadgeProps {
  reminderLevel: ReminderLevel;
  calendarStatus: CalendarStatus;
}

function ReminderBadge({ reminderLevel, calendarStatus }: ReminderBadgeProps) {
  if (calendarStatus === "completed") {
    return (
      <Badge
        variant="outline"
        className="bg-green-100 text-green-700 border-green-300"
        data-testid="status-badge-completed"
      >
        Completed
      </Badge>
    );
  }

  if (calendarStatus === "cancelled") {
    return (
      <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
        Cancelled
      </Badge>
    );
  }

  if (!reminderLevel) return null;

  const config = REMINDER_COLOURS[reminderLevel];

  if (reminderLevel === "overdue") {
    return (
      <Badge
        variant="outline"
        className={config.badge}
        data-testid="badge-overdue"
        data-reminder-level="overdue"
      >
        OVERDUE
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className={config.badge}
      data-reminder-level={reminderLevel}
    >
      {config.label}
    </Badge>
  );
}

// ============================================================================
// ADD EVENT MODAL
// ============================================================================

interface AddEventModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function AddEventModal({ open, onClose, onCreated }: AddEventModalProps) {
  const { toast } = useToast();
  const [eventType, setEventType] = useState<EventType>("renewal");
  const [eventTitle, setEventTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setEventType("renewal");
    setEventTitle("");
    setDueDate("");
    setNotes("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!eventTitle.trim() || !dueDate) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiRequest("POST", "/api/compliance/calendar", {
        eventType,
        eventTitle: eventTitle.trim(),
        dueDate,
        eventDescription: notes.trim() || undefined,
      });

      if (!response.ok) {
        throw new Error(`Failed to create event: ${response.status}`);
      }

      toast({ title: "Compliance event created" });
      onCreated();
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to create event";
      toast({ title: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent data-testid="modal-add-event" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Compliance Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="event-type">Event Type *</Label>
            <Select
              value={eventType}
              onValueChange={(v) => setEventType(v as EventType)}
            >
              <SelectTrigger data-testid="select-event-type" id="event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="event-title">Event Name *</Label>
            <Input
              id="event-title"
              data-testid="input-event-title"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              placeholder="e.g. Annual Licence Renewal"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="due-date">Due Date *</Label>
            <Input
              id="due-date"
              data-testid="input-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            data-testid="button-submit-event"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Create Event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MARK COMPLETE MODAL
// ============================================================================

interface MarkCompleteModalProps {
  event: CalendarEvent | null;
  onClose: () => void;
  onCompleted: () => void;
}

function MarkCompleteModal({
  event,
  onClose,
  onCompleted,
}: MarkCompleteModalProps) {
  const { toast } = useToast();
  const [evidenceRef, setEvidenceRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setEvidenceRef("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!event) return;

    setSubmitting(true);
    try {
      const response = await apiRequest(
        "POST",
        `/api/compliance/calendar/${event.id}/complete`,
        { evidenceReference: evidenceRef.trim() || undefined }
      );

      if (!response.ok) {
        throw new Error(`Failed to mark complete: ${response.status}`);
      }

      toast({ title: "Event marked as complete" });
      onCompleted();
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to complete event";
      toast({ title: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!event} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark Event Complete</DialogTitle>
        </DialogHeader>

        {event && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Marking <span className="font-medium text-foreground">{event.eventTitle}</span> as complete.
            </p>

            <div className="space-y-1">
              <Label htmlFor="evidence-ref">Evidence Reference (optional)</Label>
              <Input
                id="evidence-ref"
                value={evidenceRef}
                onChange={(e) => setEvidenceRef(e.target.value)}
                placeholder="e.g. Document ID or link"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? "Completing..." : "Mark Complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ACC STATUS CARD
// ============================================================================

interface AccStatusCardProps {
  accData: AccSummary[];
}

function AccStatusCard({ accData }: AccStatusCardProps) {
  const currentYear = new Date().getFullYear();
  const currentYearData = accData.find((a) => a.complianceYear === currentYear);
  const displayData = currentYearData ?? accData[accData.length - 1] ?? null;

  return (
    <Card data-testid="card-acc-status">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          ACC Checklist {displayData ? `— ${displayData.complianceYear}` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {displayData ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {displayData.completedItems} of {displayData.totalItems} items
              </span>
              <span className="font-medium">{displayData.completionPercentage}%</span>
            </div>
            <Progress
              value={displayData.completionPercentage}
              className="h-2"
              data-testid="acc-progress-bar"
            />
            <div className="pt-1">
              <a
                href="/compliance/dashboard"
                className="text-xs text-primary flex items-center gap-1 hover:underline"
              >
                View Full Checklist
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              No ACC checklist found for {currentYear}.
            </p>
            <Progress value={0} className="h-2" data-testid="acc-progress-bar" />
            <p className="text-xs text-muted-foreground">0%</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

function ComplianceCalendarContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [completingEvent, setCompletingEvent] = useState<CalendarEvent | null>(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const {
    data: eventsRaw = [],
    isLoading: eventsLoading,
  } = useQuery<CalendarEvent[]>({
    queryKey: ["/api/compliance/calendar"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/compliance/calendar");
      if (!response.ok) throw new Error("Failed to fetch calendar events");
      return response.json();
    },
  });

  const {
    data: accData = [],
    isLoading: accLoading,
  } = useQuery<AccSummary[]>({
    queryKey: ["/api/compliance/acc"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/compliance/acc");
      if (!response.ok) throw new Error("Failed to fetch ACC data");
      return response.json();
    },
  });

  // -------------------------------------------------------------------------
  // Data processing
  // -------------------------------------------------------------------------

  // Enrich events with locally computed reminderLevel if not provided
  const events: CalendarEvent[] = eventsRaw.map((e) => ({
    ...e,
    reminderLevel:
      e.reminderLevel ??
      computeReminderLevel(e.daysUntilDue, e.calendarStatus),
  }));

  // Sort: overdue first (by most overdue), then by due date ascending
  const sortedEvents = [...events].sort((a, b) => {
    const aOverdue = a.calendarStatus === "overdue";
    const bOverdue = b.calendarStatus === "overdue";

    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    // Within same group, sort by dueDate ascending
    return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
  });

  const filteredEvents = filterEvents(sortedEvents, statusFilter);

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  const handleMarkComplete = (event: CalendarEvent) => {
    setCompletingEvent(event);
  };

  const handleEventCreated = () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/compliance/calendar"] });
    toast({ title: "Calendar refreshed" });
  };

  const handleEventCompleted = () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/compliance/calendar"] });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Compliance Calendar</h1>
        <Button
          data-testid="button-add-event"
          onClick={() => setAddModalOpen(true)}
          size="sm"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Event
        </Button>
      </div>

      {/* Two-column layout: main list + ACC sidebar */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Main events list — 3 columns */}
        <div className="lg:col-span-3 space-y-3">
          {/* Status filter bar */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Filter:</span>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <SelectTrigger
                data-testid="select-filter-status"
                className="w-[160px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!eventsLoading && (
              <span className="text-sm text-muted-foreground ml-auto">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Events list */}
          {eventsLoading ? (
            <Card>
              <CardContent className="py-12 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : filteredEvents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Calendar className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No compliance events found</p>
                <p className="text-sm mt-1">
                  Click "Add Event" to create a new compliance deadline.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredEvents.map((event) => {
                const reminderLevel = event.reminderLevel;
                const rowClass =
                  reminderLevel && reminderLevel in REMINDER_COLOURS
                    ? REMINDER_COLOURS[reminderLevel].row
                    : "";

                return (
                  <Card
                    key={event.id}
                    className={rowClass}
                    data-testid={`row-event-${event.id}`}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">
                              {event.eventTitle}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0"
                            >
                              {EVENT_TYPE_LABELS[event.eventType] ??
                                event.eventType}
                            </Badge>
                            <ReminderBadge
                              reminderLevel={reminderLevel}
                              calendarStatus={event.calendarStatus}
                            />
                          </div>

                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span>Due: {formatDate(event.dueDate)}</span>
                            <span
                              className={
                                event.calendarStatus === "overdue"
                                  ? "text-red-600 font-medium"
                                  : ""
                              }
                            >
                              {getDaysLabel(event.daysUntilDue)}
                            </span>
                            {event.completedDate && (
                              <span>
                                Completed: {formatDate(event.completedDate)}
                              </span>
                            )}
                          </div>

                          {event.completionNotes && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              Note: {event.completionNotes}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {event.calendarStatus !== "completed" &&
                            event.calendarStatus !== "cancelled" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7"
                                data-testid="button-mark-complete"
                                onClick={() => handleMarkComplete(event)}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Mark Complete
                              </Button>
                            )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar — 1 column */}
        <div className="lg:col-span-1 space-y-3">
          {accLoading ? (
            <Card data-testid="card-acc-status">
              <CardContent className="py-6 flex items-center justify-center">
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : (
            <AccStatusCard accData={accData} />
          )}
        </div>
      </div>

      {/* Modals */}
      <AddEventModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={handleEventCreated}
      />

      <MarkCompleteModal
        event={completingEvent}
        onClose={() => setCompletingEvent(null)}
        onCompleted={handleEventCompleted}
      />
    </div>
  );
}

// ============================================================================
// PAGE EXPORT
// ============================================================================

export default function ComplianceCalendarPage() {
  return (
    <ComplianceLayout>
      <ComplianceCalendarContent />
    </ComplianceLayout>
  );
}
